/* Slider Pro — full-width image slideshow (sections/slider-pro.liquid).
   Native scroll-snap does the swiping; this element adds arrows, dots,
   mouse dragging, looping, keyboard support and an autoplay that pauses on
   hover, focus, touch, off-screen and hidden tabs. There is no scroll
   listener: the active slide is tracked with an IntersectionObserver. */
(function () {
  'use strict';

  if (customElements.get('slider-pro')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var MIN_INTERVAL_MS = 2000;
  var DRAG_THRESHOLD_PX = 6;
  var DRAG_COMMIT_RATIO = 0.15;
  var WRAP_FADE_MS = 220;
  var SCROLL_SETTLE_MS = 600;
  var ACTIVE_THRESHOLD = 0.6;
  var FOCUSABLE_SELECTOR = 'a[href], button, [tabindex]';

  class SliderPro extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slider-slide]'));
      if (!this.viewport || this.slides.length === 0) return;

      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.toggleButton = this.querySelector('[data-slider-toggle]');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-slider-dot]'));

      this.index = 0;
      this.interval = Math.max(parseInt(this.dataset.interval, 10) || 0, MIN_INTERVAL_MS);
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion.matches;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';

      // Anything that should hold autoplay registers a reason here; autoplay
      // only runs while the set is empty.
      this.blockers = new Set();
      this.timer = null;
      this.remaining = this.interval;
      this.startedAt = 0;

      this.pointerId = null;
      this.dragging = false;
      this.suppressClick = false;

      this.bindNavigation();
      this.bindDrag();
      this.bindAutoplay();
      this.bindEditor();
      this.observeSlides();

      this.setActive(0, true);
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* --------------------------------------------------------------
       Navigation
    -------------------------------------------------------------- */

    bindNavigation() {
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.goTo(this.index + 1));

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
      });
    }

    goTo(index, behavior) {
      var count = this.slides.length;
      if (count < 2) return;

      if (index < 0 || index >= count) {
        this.wrapTo(index < 0 ? count - 1 : 0);
        return;
      }
      this.scrollToSlide(index, behavior);
    }

    scrollToSlide(index, behavior) {
      var slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({
        left: slide.offsetLeft,
        behavior: behavior || (reducedMotion.matches ? 'auto' : 'smooth'),
      });
    }

    /* Jumping from the last slide to the first (or back) crossfades the
       viewport instead of scrolling through every slide in between. */
    wrapTo(index) {
      if (this.classList.contains('is-wrapping')) return;

      var fade = reducedMotion.matches ? 0 : WRAP_FADE_MS;
      this.classList.add('is-wrapping');
      window.setTimeout(() => {
        this.scrollToSlide(index, 'auto');
        window.requestAnimationFrame(() => this.classList.remove('is-wrapping'));
      }, fade);
    }

    observeSlides() {
      if (!('IntersectionObserver' in window)) return;

      this.slideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.viewport, threshold: ACTIVE_THRESHOLD },
      );
      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    setActive(index, force) {
      if (index < 0 || (index === this.index && !force)) return;
      this.index = index;

      this.slides.forEach((slide, i) => {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        // Off-screen slides are removed from the tab order so keyboard focus
        // never lands on something the visitor cannot see.
        slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
          if (active) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      this.preloadImage(index + 1);
      this.preloadImage(index - 1);
      this.restartTimer();
    }

    /* Slides beyond the second are lazy-loaded in the markup; promote the
       neighbours of the active slide so they are ready before they appear. */
    preloadImage(index) {
      var slide = this.slides[index];
      if (!slide) return;
      var image = slide.querySelector('img[loading="lazy"]');
      if (image) image.setAttribute('loading', 'eager');
    }

    /* --------------------------------------------------------------
       Mouse dragging (touch already scrolls natively)
    -------------------------------------------------------------- */

    bindDrag() {
      this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());

      // A drag must not trigger the link of the slide it started on.
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        true,
      );
    }

    onPointerDown(event) {
      this.block('pointer');
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.viewport.scrollLeft;
      this.dragging = false;
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      var delta = event.clientX - this.dragStartX;
      if (!this.dragging) {
        if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        this.dragging = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture(event.pointerId);
      }
      this.viewport.scrollLeft = this.dragStartScroll - delta;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) {
        this.unblock('pointer');
        return;
      }

      this.pointerId = null;
      if (!this.dragging) {
        this.unblock('pointer');
        return;
      }

      this.dragging = false;
      this.suppressClick = true;
      window.setTimeout(() => (this.suppressClick = false), 0);

      var moved = this.viewport.scrollLeft - this.dragStartScroll;
      var commit = this.viewport.clientWidth * DRAG_COMMIT_RATIO;
      var target = this.index;
      if (moved > commit) target += 1;
      if (moved < -commit) target -= 1;

      // Snapping stays off until the scroll lands on the target so the
      // browser does not re-snap to a different slide midway.
      this.afterScrollSettles(() => {
        this.viewport.classList.remove('is-dragging');
        this.unblock('pointer');
      });
      this.goTo(target);
    }

    afterScrollSettles(callback) {
      var done = false;
      var finish = () => {
        if (done) return;
        done = true;
        this.viewport.removeEventListener('scrollend', finish);
        callback();
      };
      if ('onscrollend' in window) this.viewport.addEventListener('scrollend', finish);
      window.setTimeout(finish, SCROLL_SETTLE_MS);
    }

    /* --------------------------------------------------------------
       Autoplay
    -------------------------------------------------------------- */

    bindAutoplay() {
      if (!this.autoplayEnabled) return;
      this.classList.add('is-playing');

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.block('hover'));
        this.addEventListener('mouseleave', () => this.unblock('hover'));
      }
      // Only keyboard focus holds autoplay: a mouse click leaves focus on the
      // dot/arrow it hit and must not pause the show indefinitely.
      this.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.block('focus');
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.unblock('focus');
      });

      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', this.onToggle.bind(this));
      }

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.block('hidden');
        } else {
          this.unblock('hidden');
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.unblock('offscreen');
              } else {
                this.block('offscreen');
              }
            });
          },
          { threshold: 0.25 },
        );
        this.visibilityObserver.observe(this);
      }
    }

    onToggle() {
      var paused = this.classList.toggle('is-user-paused');
      this.classList.toggle('is-playing', !paused);
      if (paused) {
        this.block('user');
      } else {
        this.unblock('user');
      }
      if (this.toggleButton) {
        this.toggleButton.setAttribute(
          'aria-label',
          paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause,
        );
      }
    }

    block(reason) {
      this.blockers.add(reason);
      this.syncTimer();
    }

    unblock(reason) {
      this.blockers.delete(reason);
      this.syncTimer();
    }

    syncTimer() {
      if (this.autoplayEnabled && this.blockers.size === 0) {
        this.startTimer();
      } else {
        this.pauseTimer();
      }
    }

    startTimer() {
      if (this.timer || !this.autoplayEnabled) return;
      this.startedAt = Date.now();
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.remaining = this.interval;
        this.goTo(this.index + 1);
        // setActive() restarts the countdown once the new slide is visible;
        // this keeps autoplay alive if that observation never fires.
        this.syncTimer();
      }, this.remaining);
      this.classList.remove('is-paused');
    }

    /* Keeps the time already elapsed so a hover does not reset the
       countdown (the dot progress animation is paused in sync via CSS). */
    pauseTimer() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
      this.remaining = Math.max(this.remaining - (Date.now() - this.startedAt), 100);
      this.classList.add('is-paused');
    }

    stopTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    restartTimer() {
      if (!this.autoplayEnabled) return;
      this.stopTimer();
      this.remaining = this.interval;
      this.syncTimer();
    }

    /* --------------------------------------------------------------
       Theme editor
    -------------------------------------------------------------- */

    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      this.onBlockSelect = (event) => {
        var index = this.slides.indexOf(event.target);
        if (index === -1) return;
        this.goTo(index, 'auto');
        this.block('editor');
      };
      this.onBlockDeselect = (event) => {
        if (this.slides.indexOf(event.target) === -1) return;
        this.unblock('editor');
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  customElements.define('slider-pro', SliderPro);
})();
